import React from 'react';
import { SENTRY_ENABLED, Sentry } from '@/monitoring';

const Fallback = ({ resetError }) => (
  <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#FBF9F6' }}>
    <div className="text-center max-w-md" data-testid="error-boundary-fallback">
      <div
        className="w-14 h-14 rounded-full mx-auto mb-8"
        style={{ background: 'radial-gradient(circle, rgba(201,168,76,0.4), transparent)' }}
      />
      <h1 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
        Something needs a moment.
      </h1>
      <p className="text-sm mb-8 leading-relaxed" style={{ fontFamily: 'Jost, sans-serif', color: '#5C5968' }}>
        We've been notified and are looking into it. Nothing you did caused this — take a breath, and try again when you're ready.
      </p>
      <button
        onClick={resetError}
        className="px-8 py-3 rounded-full text-sm transition-all duration-200 active:scale-[0.97]"
        style={{ backgroundColor: '#C9A84C', color: '#FBF9F6', fontFamily: 'Jost, sans-serif' }}
        data-testid="error-boundary-retry"
      >
        Try again
      </button>
    </div>
  </div>
);

/**
 * Plain React error boundary used as the base case when Sentry is disabled
 * (no SENTRY_DSN). Prevents a blank white screen on uncaught render errors.
 */
class LocalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.resetError = this.resetError.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Sentry is off; log to console for local visibility only.
    // eslint-disable-next-line no-console
    console.error('Unhandled render error:', error, info);
  }

  resetError() {
    this.setState({ hasError: false });
  }

  render() {
    if (this.state.hasError) {
      return <Fallback resetError={this.resetError} />;
    }
    return this.props.children;
  }
}

export default function MonitoringBoundary({ children }) {
  if (!SENTRY_ENABLED) {
    return <LocalErrorBoundary>{children}</LocalErrorBoundary>;
  }
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <Fallback resetError={resetError} />}
      beforeCapture={(scope) => scope.setTag('app', 'mindful-renewal-web')}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
